import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { geocodeAddress, approximateCoords } from "@/lib/geocoding";
import {
  User, Briefcase, ArrowRight, ArrowLeft, Mail, Lock, Phone, MapPin,
  CheckCircle2, MailCheck, Loader2, Eye, EyeOff, Camera, CreditCard,
  ShieldCheck, Upload, BadgeCheck,
} from "lucide-react";
import type { Database } from "@/integrations/supabase/types";

type AppRole = Database["public"]["Enums"]["app_role"];

const MONTHLY_PRICE_ID = "price_1UACAw7YfeC0DVSzjgjjkDV8";
const ANNUAL_PRICE_ID = "price_1UAC9m7YfeC0DVSzxKNM4g1s";

interface AuthFormCardProps {
  initialTab: "login" | "register";
  initialRole: "parent" | "pro";
  resumeOnboarding?: boolean;
}

interface ProDraft {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  homeAddress?: string;
  avatarDataUrl?: string;
}

const DRAFT_KEY = "clickandgard_pro_onboarding_draft";
const RESUME_KEY = "clickandgard_pro_onboarding_resume";

const safeReadDraft = (): ProDraft => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) as ProDraft : {};
  } catch {
    return {};
  }
};

const splitName = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return { firstName: parts.shift() ?? "", lastName: parts.join(" ") };
};

const AuthFormCard = ({ initialTab, initialRole, resumeOnboarding = false }: AuthFormCardProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signUp, signIn, role, user } = useAuth();
  const { getSetting } = useSiteSettings();
  const googleAuthEnabled = getSetting("google_auth_enabled", "true") === "true";
  const subscriptionRequired = getSetting("pro_subscription_enabled", "true") !== "false";
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [tab, setTab] = useState(initialTab);
  const [selectedRole, setSelectedRole] = useState<"parent" | "pro">(initialRole);
  const [step, setStep] = useState(initialRole === "pro" && resumeOnboarding ? 2 : 1);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [homeAddress, setHomeAddress] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [avatarPreview, setAvatarPreview] = useState("");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarDataUrl, setAvatarDataUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<"monthly" | "annual">("annual");

  const isProRegistration = tab === "register" && selectedRole === "pro";
  const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();

  useEffect(() => {
    if (tab === "login") {
      try {
        const remembered = localStorage.getItem("clickandgard_remember_email");
        if (remembered) {
          setEmail(remembered);
          setRememberMe(true);
        }
      } catch {
        // Local storage can be disabled by the browser.
      }
    }
  }, [tab]);

  useEffect(() => {
    if (!resumeOnboarding || !user || role !== "pro") return;
    let cancelled = false;

    const hydrateOnboarding = async () => {
      const draft = safeReadDraft();
      const [{ data: profile }, { data: ownPhone }, { data: privateProfile }] = await Promise.all([
        supabase.from("profiles").select("full_name, avatar_url").eq("user_id", user.id).maybeSingle(),
        supabase.rpc("get_my_phone"),
        supabase.rpc("get_my_private_profile"),
      ]);
      if (cancelled) return;
      const privateRow = Array.isArray(privateProfile) ? privateProfile[0] : privateProfile;
      const name = splitName(profile?.full_name ?? "");
      setFirstName((current) => current || draft.firstName || name.firstName);
      setLastName((current) => current || draft.lastName || name.lastName);
      setEmail((current) => current || draft.email || user.email || "");
      setPhone((current) => current || draft.phone || ownPhone || "");
      setHomeAddress((current) => current || draft.homeAddress || privateRow?.street_address || "");
      setAvatarUrl(profile?.avatar_url || "");
      if (!profile?.avatar_url && draft.avatarDataUrl) {
        setAvatarDataUrl(draft.avatarDataUrl);
        setAvatarPreview(draft.avatarDataUrl);
      }
      // Le compte existe déjà : si l'abonnement est requis, on reprend
      // directement à l'étape abonnement. Sinon, on poursuit avec le profil.
      setStep(subscriptionRequired ? 2 : 3);
    };

    void hydrateOnboarding();
    return () => { cancelled = true; };
    // This effect intentionally hydrates once for a resumed session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeOnboarding, user, role]);

  useEffect(() => {
    if (selectedRole === "parent") setStep(1);
  }, [selectedRole]);

  const handleGoogleSignIn = async () => {
    if (googleLoading) return;
    setGoogleLoading(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", { redirect_uri: window.location.origin });
      if (result.error) {
        const errorMsg = result.error instanceof Error ? result.error.message : String(result.error);
        toast({ title: "Erreur de connexion Google", description: errorMsg, variant: "destructive" });
        setGoogleLoading(false);
        return;
      }
      if (!result.redirected) toast({ title: "Connexion réussie", description: "Bienvenue sur Click&Gard !" });
    } catch {
      toast({ title: "Erreur de connexion Google", description: "Veuillez réessayer.", variant: "destructive" });
      setGoogleLoading(false);
    }
  };

  const handleForgotPassword = async (event: FormEvent) => {
    event.preventDefault();
    if (loading) return;
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, { redirectTo: `${window.location.origin}/reset-password` });
    setLoading(false);
    if (error) toast({ title: "Erreur", description: error.message, variant: "destructive" });
    else {
      toast({ title: "Email envoyé", description: "Vérifiez votre boîte mail pour réinitialiser votre mot de passe." });
      setForgotMode(false);
    }
  };

  const validateAccount = () => {
    if (!firstName.trim() || !lastName.trim()) return "Indiquez votre prénom et votre nom.";
    if (!email.trim()) return "Indiquez votre adresse email.";
    if (!phone.trim()) return "Indiquez votre numéro de téléphone.";
    if (password.length < 8) return "Le mot de passe doit contenir au moins 8 caractères.";
    if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) return "Mélangez lettres et chiffres dans votre mot de passe.";
    return null;
  };

  const getAvatarFile = async () => {
    if (avatarFile) return avatarFile;
    if (!avatarDataUrl) return null;
    try {
      const response = await fetch(avatarDataUrl);
      const blob = await response.blob();
      return new File([blob], "avatar.jpg", { type: blob.type || "image/jpeg" });
    } catch {
      return null;
    }
  };

  const handleAvatarChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "Format invalide", description: "Veuillez sélectionner une image.", variant: "destructive" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Image trop volumineuse", description: "L'image ne doit pas dépasser 5 Mo.", variant: "destructive" });
      return;
    }
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    try {
      const reader = new FileReader();
      reader.onload = () => setAvatarDataUrl(typeof reader.result === "string" ? reader.result : "");
      reader.readAsDataURL(file);
    } catch {
      setAvatarDataUrl("");
    }
  };

  const saveProEssentials = async (userId: string) => {
    const file = await getAvatarFile();
    let nextAvatarUrl = avatarUrl;
    if (!nextAvatarUrl && file) {
      const extension = file.type.split("/")[1]?.replace("jpeg", "jpg") || "jpg";
      const path = `${userId}/avatar.${extension}`;
      const { error: uploadError } = await supabase.storage.from("avatars").upload(path, file, { upsert: true, contentType: file.type });
      if (uploadError) throw uploadError;
      const { data: publicUrl } = supabase.storage.from("avatars").getPublicUrl(path);
      nextAvatarUrl = `${publicUrl.publicUrl}?t=${Date.now()}`;
    }
    if (!nextAvatarUrl) throw new Error("Ajoutez une photo de profil pour continuer.");

    const geo = await geocodeAddress(homeAddress.trim());
    if (!geo) throw new Error("Adresse introuvable. Vérifiez l'adresse complète saisie.");
    const approximate = approximateCoords(geo.latitude, geo.longitude);

    const { error: profileError } = await supabase.from("profiles").update({
      full_name: fullName,
      avatar_url: nextAvatarUrl,
      phone: phone.trim(),
      city: geo.city,
      postal_code: geo.postcode,
      latitude: approximate.latitude,
      longitude: approximate.longitude,
    // Supabase generated types do not yet include every profile field used by the onboarding flow.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any).eq("user_id", userId);
    if (profileError) throw profileError;

    // pro_addresses is present in production but not yet represented in the generated client types.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: addressError } = await supabase.from("pro_addresses" as any).upsert({
      user_id: userId,
      street_address: homeAddress.trim(),
      postal_code: geo.postcode,
      city: geo.city,
      full_address: geo.label,
      latitude: geo.latitude,
      longitude: geo.longitude,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any, { onConflict: "user_id" });
    if (addressError) throw addressError;
    window.dispatchEvent(new Event("pro-access:refresh"));
    return nextAvatarUrl;
  };

  const persistDraft = () => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ firstName, lastName, email, phone, homeAddress, avatarDataUrl } satisfies ProDraft));
      localStorage.setItem(RESUME_KEY, "true");
    } catch {
      // The server remains the source of truth; this is only a resume convenience.
    }
  };

  const createProAccount = async () => {
    setLoading(true);
    try {
      const { error } = await signUp(email.trim(), password, fullName, "pro" as AppRole, "professionnel");
      if (error) {
        const message = error.message.toLowerCase().includes("pwned") || error.message.toLowerCase().includes("compromised")
          ? "Ce mot de passe est trop courant. Choisissez-en un autre."
          : error.message;
        throw new Error(message);
      }

      // Le compte est désormais créé immédiatement, même si le PRO quitte ensuite le tunnel.
      persistDraft();

      const { data: sessionData } = await supabase.auth.getSession();
      const sessionUser = sessionData.session?.user ?? null;
      if (!sessionUser) {
        setRegisteredEmail(email.trim());
        setShowEmailConfirmation(true);
        toast({
          title: "Compte PRO créé ✅",
          description: "Confirmez votre email puis activez votre abonnement PRO. Votre compte est déjà enregistré sur Click&Gard.",
        });
        return;
      }

      // Si la confirmation email n'est pas bloquante, on conserve déjà les bases du profil.
      const { error: profileError } = await supabase.from("profiles").update({
        full_name: fullName,
        phone: phone.trim(),
      // Supabase generated types lag behind the current profiles schema.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any).eq("user_id", sessionUser.id);
      if (profileError) console.warn("Pro basics save failed:", profileError.message);

      setStep(subscriptionRequired ? 2 : 3);
      toast({
        title: "Compte PRO créé ✅",
        description: subscriptionRequired
          ? "Choisissez maintenant votre abonnement PRO."
          : "Complétez maintenant votre profil professionnel.",
      });
    } catch (error) {
      toast({
        title: "Impossible de créer le compte PRO",
        description: error instanceof Error ? error.message : "Vérifiez les informations saisies.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const finishProOnboarding = async (subscriptionType: "monthly" | "annual") => {
    setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const sessionUser = user ?? sessionData.session?.user ?? null;
      if (!sessionUser) {
        persistDraft();
        toast({ title: "Reconnectez-vous", description: "Votre compte PRO existe déjà. Connectez-vous pour terminer l'abonnement.", variant: "destructive" });
        setTab("login");
        return;
      }

      const { data, error: checkoutError } = await supabase.functions.invoke("create-checkout", {
        body: { price_id: subscriptionType === "annual" ? ANNUAL_PRICE_ID : MONTHLY_PRICE_ID, subscription_type: subscriptionType },
      });
      if (checkoutError) throw checkoutError;
      if (!data?.url) throw new Error("Impossible de démarrer l'abonnement.");

      // Redirection dans l'onglet courant : évite les blocages de pop-up après les appels async.
      window.location.assign(data.url);
    } catch (error) {
      toast({ title: "Impossible de finaliser", description: error instanceof Error ? error.message : "Vérifiez les informations saisies.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (loading) return;

    if (tab === "register" && isProRegistration) {
      if (step === 1) {
        const validationError = validateAccount();
        if (validationError) {
          toast({ title: "Vérifiez vos informations", description: validationError, variant: "destructive" });
          return;
        }
        await createProAccount();
        return;
      }
      if (step === 2) {
        await finishProOnboarding(selectedPlan);
        return;
      }
      if (!homeAddress.trim() || (!avatarPreview && !avatarUrl)) {
        toast({ title: "Deux informations sont nécessaires", description: "Ajoutez votre adresse complète et votre photo de profil.", variant: "destructive" });
        return;
      }

      setLoading(true);
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const sessionUser = user ?? sessionData.session?.user ?? null;
        if (!sessionUser) {
          persistDraft();
          toast({ title: "Reconnectez-vous", description: "Votre compte PRO existe déjà. Connectez-vous pour reprendre votre inscription.", variant: "destructive" });
          setTab("login");
          return;
        }
        await saveProEssentials(sessionUser.id);
        persistDraft();
        navigate("/settings?tab=verification");
        toast({ title: "Profil essentiel enregistré ✅", description: "Vous pouvez maintenant finaliser votre vérification d'identité." });
      } catch (error) {
        toast({ title: "Profil non enregistré", description: error instanceof Error ? error.message : "Vérifiez votre adresse et votre photo.", variant: "destructive" });
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    if (tab === "register") {
      if (!firstName.trim() || !lastName.trim() || !email.trim()) {
        toast({ title: "Champs requis", description: "Indiquez votre prénom, votre nom et votre email.", variant: "destructive" });
        setLoading(false);
        return;
      }
      if (password.length < 8 || !/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) {
        toast({ title: "Mot de passe trop simple", description: "Utilisez au moins 8 caractères avec des lettres et des chiffres.", variant: "destructive" });
        setLoading(false);
        return;
      }
      const { error } = await signUp(email.trim(), password, `${firstName.trim()} ${lastName.trim()}`, "parent" as AppRole);
      if (error) toast({ title: "Erreur lors de l'inscription", description: error.message, variant: "destructive" });
      else {
        setRegisteredEmail(email.trim());
        setShowEmailConfirmation(true);
        toast({ title: "Compte créé", description: "Vérifiez votre email pour activer votre compte." });
      }
    } else {
      if (!email.trim() || !password.trim()) {
        toast({ title: "Champs requis", description: "Veuillez remplir tous les champs.", variant: "destructive" });
        setLoading(false);
        return;
      }
      try {
        if (!rememberMe) {
          sessionStorage.setItem("clickandgard_no_persist", "true");
          localStorage.removeItem("clickandgard_remember_email");
        } else {
          sessionStorage.removeItem("clickandgard_no_persist");
          localStorage.setItem("clickandgard_remember_email", email.trim());
        }
      } catch {
        // Ignore preference storage failures.
      }
      const { error } = await signIn(email.trim(), password);
      if (error) {
        const msg = error.message.toLowerCase();
        const title = msg.includes("email not confirmed") || msg.includes("email_not_confirmed")
          ? "Email non vérifié"
          : msg.includes("invalid login credentials") || msg.includes("invalid_credentials")
            ? "Identifiants incorrects"
            : "Erreur de connexion";
        toast({ title, description: error.message, variant: "destructive" });
      } else toast({ title: "Connexion réussie", description: "Bienvenue sur Click&Gard !" });
    }
    setLoading(false);
  };


  if (showEmailConfirmation) {
    return (
      <div className="w-full max-w-xl mx-auto">
        <div className="bg-card rounded-2xl shadow-xl border border-border/40 p-7 sm:p-10 space-y-6 animate-scale-in text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
            <MailCheck className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-2xl font-bold text-foreground">Vérifiez votre email</h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Un lien de confirmation a été envoyé à <span className="font-semibold text-foreground">{registeredEmail}</span>.
            <br />Après confirmation, reprenez votre inscription : votre compte PRO est déjà créé et vous passerez directement à l'abonnement.
          </p>
          <div className="flex items-center gap-2 p-3 rounded-xl bg-primary/5 border border-primary/15 text-left">
            <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
            <p className="text-xs text-muted-foreground">Vérifiez aussi le dossier des courriers indésirables.</p>
          </div>
          <Button className="w-full h-12 rounded-xl font-semibold" onClick={() => { setShowEmailConfirmation(false); setTab("login"); }}>
            J'ai confirmé, me connecter <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  const progressSteps = isProRegistration
    ? [{ short: "Compte", long: "1. Compte" }, { short: "Abonnement", long: "2. Abonnement" }, { short: "Profil", long: "3. Profil essentiel" }]
    : [{ short: "Compte", long: "1. Compte" }];

  return (
    <div className="w-full max-w-xl mx-auto">
      <div className="flex items-center justify-center gap-3 mb-6 lg:hidden">
        <img alt="Click&Gard" className="w-11 h-11 rounded-xl object-cover shadow-md" src="/lovable-uploads/2236e7c0-4083-436f-a95c-06d4486b46d6.png" />
        <span className="text-xl font-bold text-foreground tracking-tight">Click<span className="text-primary">&</span>Gard</span>
      </div>

      <div className="bg-card rounded-2xl shadow-xl border border-border/40 p-6 sm:p-9 animate-scale-in">
        <div className="text-center space-y-1.5 mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
            {tab === "login" ? "Bon retour !" : isProRegistration ? "Votre inscription PRO" : "Créez votre compte"}
          </h2>
          <p className="text-muted-foreground text-sm">
            {tab === "login" ? "Connectez-vous pour accéder à votre espace" : isProRegistration ? "Créez votre compte, activez votre abonnement, puis finalisez votre profil." : "Une inscription simple et rapide"}
          </p>
        </div>

        <div className="flex rounded-xl bg-muted p-1 gap-1 mb-6">
          <Button type="button" variant={tab === "login" ? "default" : "ghost"} className="flex-1 h-10 rounded-lg" onClick={() => setTab("login")}>Connexion</Button>
          <Button type="button" variant={tab === "register" ? "default" : "ghost"} className="flex-1 h-10 rounded-lg" onClick={() => setTab("register")}>Inscription</Button>
        </div>

        {tab === "register" && (
          <div className="mb-7" aria-label="Progression de l'inscription">
            <div className="flex items-center gap-1.5 sm:gap-2">
              {progressSteps.map((stepInfo, index) => {
                const number = index + 1;
                const done = isProRegistration ? step > number : false;
                const current = isProRegistration ? step === number : number === 1;
                return (
                  <div key={stepInfo.long} className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0">
                    <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-xs font-bold border-2 ${done ? "bg-primary border-primary text-primary-foreground" : current ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground"}`}>
                      {done ? <CheckCircle2 className="w-4 h-4" /> : number}
                    </div>
                    <span className={`text-[11px] sm:text-xs font-semibold truncate ${current ? "inline" : "hidden sm:inline"} ${current || done ? "text-foreground" : "text-muted-foreground"}`}><span className="sm:hidden">{stepInfo.short}</span><span className="hidden sm:inline">{stepInfo.long}</span></span>
                    {index < progressSteps.length - 1 && <div className={`h-0.5 flex-1 min-w-2 ${done ? "bg-primary" : "bg-border"}`} />}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {tab === "register" && step === 1 && (
            <>
              <div className="space-y-2">
                <Label>Je m'inscris comme</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Button type="button" variant={selectedRole === "parent" ? "default" : "outline"} className="h-auto min-h-20 flex-col gap-2 rounded-xl" aria-pressed={selectedRole === "parent"} onClick={() => setSelectedRole("parent")}>
                    <User className="w-5 h-5" /> Parent
                  </Button>
                  <Button type="button" variant={selectedRole === "pro" ? "secondary" : "outline"} className="h-auto min-h-20 flex-col gap-2 rounded-xl" aria-pressed={selectedRole === "pro"} onClick={() => setSelectedRole("pro")}>
                    <Briefcase className="w-5 h-5" /> Professionnel
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2"><Label htmlFor="firstName">Prénom</Label><Input id="firstName" value={firstName} onChange={(event) => setFirstName(event.target.value)} placeholder="Marie" autoComplete="given-name" required className="h-12 rounded-xl" /></div>
                <div className="space-y-2"><Label htmlFor="lastName">Nom</Label><Input id="lastName" value={lastName} onChange={(event) => setLastName(event.target.value)} placeholder="Dupont" autoComplete="family-name" required className="h-12 rounded-xl" /></div>
              </div>
              <div className="space-y-2"><Label htmlFor="email">Email</Label><div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input id="email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="marie@exemple.fr" autoComplete="email" required className="pl-10 h-12 rounded-xl" /></div></div>
              {isProRegistration && <div className="space-y-2"><Label htmlFor="phone">Téléphone</Label><div className="relative"><Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input id="phone" value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="06 12 34 56 78" autoComplete="tel" maxLength={20} required className="pl-10 h-12 rounded-xl" /></div></div>}
              <div className="space-y-2"><Label htmlFor="password">Mot de passe</Label><div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} placeholder="8 caractères minimum" autoComplete="new-password" required minLength={8} className="pl-10 pr-11 h-12 rounded-xl" /><Button type="button" variant="ghost" size="icon" aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"} className="absolute right-1 top-1/2 -translate-y-1/2" onClick={() => setShowPassword((visible) => !visible)}>{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</Button></div><p className="text-xs text-muted-foreground">8 caractères minimum, avec lettres et chiffres.</p></div>
            </>
          )}

          {tab === "register" && isProRegistration && step === 3 && (
            <>
              <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 flex gap-3"><MapPin className="w-5 h-5 text-primary shrink-0 mt-0.5" /><p className="text-sm text-muted-foreground">Votre adresse reste privée et sert uniquement à vous proposer des gardes proches.</p></div>
              <div className="space-y-2"><Label htmlFor="homeAddress">Adresse complète de domicile</Label><Input id="homeAddress" value={homeAddress} onChange={(event) => setHomeAddress(event.target.value)} placeholder="12 rue des Lilas, 75015 Paris" autoComplete="street-address" required className="h-12 rounded-xl" /></div>
              <div className="space-y-3"><Label>Photo de profil</Label><div className="flex flex-col sm:flex-row items-center gap-4 rounded-xl border border-dashed border-border p-5"><div className="w-24 h-24 rounded-full overflow-hidden bg-muted flex items-center justify-center shrink-0">{avatarPreview || avatarUrl ? <img src={avatarPreview || avatarUrl} alt="Aperçu de votre photo de profil" className="w-full h-full object-cover" /> : <Camera className="w-8 h-8 text-muted-foreground" />}</div><div className="text-center sm:text-left"><input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" /><Button type="button" variant="outline" className="h-11 rounded-xl" onClick={() => fileInputRef.current?.click()}><Upload className="w-4 h-4 mr-2" /> Ajouter ma photo</Button><p className="text-xs text-muted-foreground mt-2">JPG, PNG ou WEBP · 5 Mo maximum</p></div></div></div>
            </>
          )}

          {tab === "register" && isProRegistration && step === 2 && (
            <>
              <div className="flex items-start gap-3"><div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><CreditCard className="w-5 h-5 text-primary" /></div><div><h3 className="font-bold text-foreground">Activez votre accès PRO</h3><p className="text-sm text-muted-foreground">Votre compte est créé. Activez maintenant votre abonnement pour débloquer l'accès aux annonces et poursuivre votre inscription.</p></div></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button type="button" variant={selectedPlan === "monthly" ? "default" : "outline"} className="h-auto min-h-32 flex-col items-start text-left p-5 rounded-xl" onClick={() => setSelectedPlan("monthly")}><span className="text-sm font-semibold">Mensuel</span><span className="text-2xl font-bold mt-1">6,99 €<span className="text-sm font-normal opacity-80"> / mois</span></span><span className="text-xs opacity-80 mt-1">Renouvellement mensuel</span></Button>
                <Button type="button" variant={selectedPlan === "annual" ? "default" : "outline"} className="relative h-auto min-h-32 flex-col items-start text-left p-5 rounded-xl border-primary" onClick={() => setSelectedPlan("annual")}><span className="absolute -top-3 right-3 rounded-full bg-secondary px-2.5 py-1 text-[10px] font-bold text-secondary-foreground">Le plus avantageux</span><span className="text-sm font-semibold">Annuel</span><span className="text-2xl font-bold mt-1">59,88 €<span className="text-sm font-normal opacity-80"> / an</span></span><span className="text-xs opacity-80 mt-1">Soit 4,99 €/mois · Économisez 24 € / an</span></Button>
              </div>
              <div className="flex items-start gap-2 text-xs text-muted-foreground"><ShieldCheck className="w-4 h-4 text-primary shrink-0 mt-0.5" /><p>Renouvellement automatique à chaque échéance. Résiliez depuis Click&Gard au moins 24 h avant le renouvellement pour empêcher l'échéance suivante.</p></div>
            </>
          )}

          {tab === "login" && <><div className="space-y-2"><Label htmlFor="login-email">Email</Label><div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input id="login-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="marie@exemple.fr" autoComplete="email" required className="pl-10 h-12 rounded-xl" /></div></div><div className="space-y-2"><Label htmlFor="login-password">Mot de passe</Label><div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input id="login-password" type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required className="pl-10 pr-11 h-12 rounded-xl" /><Button type="button" variant="ghost" size="icon" aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"} className="absolute right-1 top-1/2 -translate-y-1/2" onClick={() => setShowPassword((visible) => !visible)}>{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</Button></div></div><div className="flex items-center gap-2"><input type="checkbox" id="rememberMe" checked={rememberMe} onChange={(event) => setRememberMe(event.target.checked)} className="h-4 w-4 rounded border-border text-primary focus:ring-primary" /><Label htmlFor="rememberMe" className="text-sm text-muted-foreground cursor-pointer">Se souvenir de moi</Label></div></>}

          <div className="flex gap-3">
            {isProRegistration && step > 2 && <Button type="button" variant="outline" className="h-12 rounded-xl" onClick={() => setStep((current) => current - 1)} disabled={loading}><ArrowLeft className="w-4 h-4 mr-2" /> Retour</Button>}
            <Button type="submit" className="flex-1 h-12 rounded-xl text-base font-semibold gap-2 shadow-md" disabled={loading}>{loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Chargement...</> : tab === "login" ? <>Se connecter <ArrowRight className="w-4 h-4" /></> : isProRegistration ? step === 1 ? <>Créer mon compte PRO <ArrowRight className="w-4 h-4" /></> : step === 2 ? <>Activer mon abonnement <ArrowRight className="w-4 h-4" /></> : <>Enregistrer mon profil <ArrowRight className="w-4 h-4" /></> : <>Créer mon compte <ArrowRight className="w-4 h-4" /></>}</Button>
          </div>
          {tab === "login" && <Button type="button" variant="link" className="w-full text-sm" onClick={() => setForgotMode(true)}>Mot de passe oublié ?</Button>}
        </form>

        {googleAuthEnabled && tab === "login" && <div className="mt-6 space-y-4"><div className="flex items-center gap-3"><div className="flex-1 h-px bg-border" /><span className="text-xs text-muted-foreground">ou</span><div className="flex-1 h-px bg-border" /></div><Button type="button" variant="outline" className="w-full h-12 rounded-xl gap-3" onClick={handleGoogleSignIn} disabled={googleLoading || loading}>{googleLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <BadgeCheck className="w-5 h-5" />} Continuer avec Google</Button></div>}

        {forgotMode && <div className="mt-5 p-4 rounded-xl bg-muted/50 border border-border/40 space-y-3"><p className="text-sm font-semibold">Réinitialiser votre mot de passe</p><form onSubmit={handleForgotPassword} className="space-y-3"><Input type="email" placeholder="Votre adresse email" value={resetEmail} onChange={(event) => setResetEmail(event.target.value)} required /><div className="flex gap-2"><Button type="submit" size="sm" className="flex-1 rounded-xl" disabled={loading}>Envoyer le lien</Button><Button type="button" size="sm" variant="ghost" className="rounded-xl" onClick={() => setForgotMode(false)}>Annuler</Button></div></form></div>}

        <p className="text-center text-xs text-muted-foreground mt-6">En continuant, vous acceptez nos conditions d'utilisation et notre politique de confidentialité.</p>
      </div>
    </div>
  );
};

export default AuthFormCard;
